import { normalizeSearch } from "../../shared/utils/format";
import type { Shop, ShopFilters, ShopLocation } from "./types";

export function hasExactLocation(location: ShopLocation): boolean {
  return (
    typeof location.latitude === "number" &&
    Number.isFinite(location.latitude) &&
    typeof location.longitude === "number" &&
    Number.isFinite(location.longitude)
  );
}

export function parseLocationText(value: string | null): ShopLocation {
  const text = value?.trim();

  if (!text) {
    return {
      latitude: null,
      longitude: null,
      addressText: null,
      status: "missing"
    };
  }

  const match = text.match(/(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)/);
  const latitude = match ? Number(match[1]) : Number.NaN;
  const longitude = match ? Number(match[2]) : Number.NaN;

  if (
    Number.isFinite(latitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    Number.isFinite(longitude) &&
    longitude >= -180 &&
    longitude <= 180
  ) {
    return {
      latitude,
      longitude,
      addressText: text,
      status: "exact"
    };
  }

  return {
    latitude: null,
    longitude: null,
    addressText: text,
    status: "text_only"
  };
}

export function shopSearchText(shop: Shop): string {
  return normalizeSearch(
    [
      shop.shopName,
      shop.shopPhoneNumber,
      shop.shopClass,
      shop.shopGovenorate,
      shop.shopCity,
      shop.shopDistrict,
      shop.shopStreet,
      shop.shopLocation.addressText
    ].join(" ")
  );
}

export function filterAndSortShops(shops: Shop[], filters: ShopFilters): Shop[] {
  const search = normalizeSearch(filters.search);

  const filtered = shops.filter((shop) => {
    const matchesSearch = !search || shopSearchText(shop).includes(search);
    const matchesClass = filters.shopClass === "all" || shop.shopClass === filters.shopClass;
    const matchesGovernorate =
      !filters.shopGovenorate || shop.shopGovenorate === filters.shopGovenorate;
    const matchesCity = !filters.shopCity || shop.shopCity === filters.shopCity;
    const matchesDistrict =
      !filters.shopDistrict ||
      normalizeSearch(shop.shopDistrict).includes(normalizeSearch(filters.shopDistrict));
    const matchesLocation =
      filters.locationStatus === "all" || shop.shopLocation.status === filters.locationStatus;

    return (
      matchesSearch &&
      matchesClass &&
      matchesGovernorate &&
      matchesCity &&
      matchesDistrict &&
      matchesLocation
    );
  });

  return filtered.sort((a, b) => {
    if (filters.sortBy === "newest") {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }

    const aValue = normalizeSearch(a[filters.sortBy]);
    const bValue = normalizeSearch(b[filters.sortBy]);
    return aValue.localeCompare(bValue);
  });
}

export function duplicateKey(shop: Pick<Shop, "shopName" | "shopPhoneNumber">): string {
  const name = normalizeSearch(shop.shopName);
  const phone = normalizeSearch(shop.shopPhoneNumber);
  return phone ? `${name}|${phone}` : name;
}
